import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { locale } from '@iv/shared/components/dropdown-new/dropdown-new.locale';
import Utils from '@iv/shared/utils';

@Component({
  selector: 'iv-live-dropdown-new',
  templateUrl: './dropdown-new.component.html',
  styleUrls: ['./dropdown-new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DropdownNewComponent),
    multi: true
  }]
})
export class DropdownNewComponent implements ControlValueAccessor, AfterContentChecked {

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    protected elementRef: ElementRef
  ) {
  }

  @ViewChild('inputField') inputField: ElementRef;
  @ViewChild('dropdownWrapper') dropdownWrapper: ElementRef;

  public selectedValue;
  public viewValue;
  public isDropdownOpened: boolean;
  public isMac = Utils.isMac;

  private _disabled = false;

  @Input() isAutocomplete: boolean = false

  @Input()
  public label: string;

  @Input()
  public listValues: any[] = [];

  public listValuesAutocomplete:any[] = []
  public error = false;

  // Показываем нужное поле из объекта
  @Input()
  public templateField: {
    list: string, // Поле, которое нужно отобразить в списке
    input: string // В инпуте
  };

  private _contentWidth: boolean;
  @Input()
  public set contentWidth(value: '' | boolean) {
    if (value === '' || value === true) {
      this._contentWidth = true;
    }
  }

  public get contentWidth() {
    return this._contentWidth;
  }

  @Input()
  public rubberInput: boolean;

  private _placeholder: string;
  @Input()
  public set placeholder(value: any) {
    if (!value) {
      this._placeholder = locale.defaultPlaceholder;
      return;
    }

    this._placeholder = value;
  }

  public get placeholder() {
    return this.selectedValue ? null : this._placeholder;
  }

  private _withoutHover: boolean;
  @Input()
  public set withoutHover(value: '' | boolean) {
    if (value === '' || value === true) {
      this._withoutHover = true;
    }
  }

  public get withoutHover() {
    return this._withoutHover;
  }

  private _withoutDivider: boolean;
  @Input()
  public set withoutDivider(value: '' | boolean) {
    if (value === '' || value === true) {
      this._withoutDivider = true;
    }
  }

  public get withoutDivider() {
    return this._withoutDivider;
  }

  private _fullyDisclosed: boolean;
  @Input() // Полностью раскрыть список
  public set fullyDisclosed(value: '' | boolean) {
    this._fullyDisclosed = !!(value === '' || value === true);
  }
  public get fullyDisclosed() {
    return this._fullyDisclosed;
  }

  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    this._disabled = value;
  }

  public get height(): string {
    if (this.isAutocomplete) {
      return this.listValuesAutocomplete.length > 4 ? '150px' : this.listValuesAutocomplete.length * 30 + 'px'
    }

    if (this.fullyDisclosed) {
      return this.listValues.length * 30 + 'px';
    }

    return this.listValues.length > 4 ? '150px' : this.listValues.length * 30 + 'px';
  }

  @Output() onStrikeChanged = new EventEmitter<string>();

  ngOnChanges(changes: SimpleChanges) :void {
    if (changes.listValues && this.listValues) this.listValuesAutocomplete = [...this.listValues]
  }

  public ngAfterContentChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  public selectItem(insideValue: any): void {
    this.selectedValue = insideValue;
    this.viewValue = this.templateField ? insideValue[this.templateField.input] : insideValue;
    this.onChange(this.selectedValue);
    this.onTouched();
    this.closeDropdown();
    if (this.isAutocomplete) {
      this.error = false;
      this.dropdownWrapper.nativeElement.style.animation = 'blinkSuccess 0.8s 1 ease';
      this.onStrikeChanged.emit('valid')
    }
  }

  public openDropdown(): void {
    if (this.disabled) return;

    if (this.isDropdownOpened) {
      this.closeDropdown();
      return;
    }

    this.isDropdownOpened = true;
  }

  public closeDropdown(): void {
    this.isDropdownOpened = false;
  }

  @HostListener('document:click', ['$event'])
  private clickOutsideDropdown(event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  // ----------- Методы ControlValueAccessor -----------
  // Вызовет форма, если значение изменилось извне
  public writeValue(outsideValue: any): void {
    if (!outsideValue) return;

    this.selectedValue = outsideValue;
    this.viewValue = this.templateField ? outsideValue[this.templateField.input] : outsideValue;
  }

  // Сохраняем обратный вызов для изменений
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Сохраняем обратный вызов для "касаний"
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Вызовем когда значение изменится
  private onChange(_: any): void {
  }

  // Вызовем при любом действии пользователя с контроллом
  private onTouched(): void {
  }

  // Установка состояния disabled
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public setAutocompleteList(inputText:string): void {
    if (!this.isDropdownOpened) this.openDropdown()
    this.listValuesAutocomplete = []
    this.listValues.forEach((value:any) => {
      if (value.description.includes(inputText)) {
        this.listValuesAutocomplete.push(value)
      }
    })
    const isValueValid = this.checkIfValueExistInList(inputText)
    if (isValueValid) {
      this.setSelectedValue(inputText)
    }
  }

  private setSelectedValue(inputText) {
    this.selectedValue = {description: inputText, name: Number(inputText) }
    this.onChange(this.selectedValue);
    this.onTouched();
  }

  private checkIfValueExistInList(inputText): boolean {
    let error = true
    this.listValues.forEach((value:any) => {
      if (value.description === inputText) {
        error = false
      }
    })
    if (error) {
      this.onStrikeChanged.emit('There is no data for selected strike')
      this.error = true;
      this.dropdownWrapper.nativeElement.style.animation = 'none'
      return false
    } else {
      this.onStrikeChanged.emit('valid')
      this.error = false
      this.closeDropdown()
      this.dropdownWrapper.nativeElement.style.animation = 'blinkSuccess 0.8s 1 ease'
      return true
    }
  }

  public openAutocomplete(): void {
    if (this.isAutocomplete) {
      this.inputField.nativeElement.focus();
    }
  }

  public onInputEnter(): void {
    const isValid = this.checkIfValueExistInList(this.inputField.nativeElement.value)
    if (isValid) {
      this.closeDropdown()
      this.inputField.nativeElement.blur()
    }
  }
}
